/** Dashboard figures — same rules as the original Dashboard (last 7 days, stale = no Project Log for 2+ days). */
import type { Backup, Member, Project, ProjectUpdate } from '@/types/models';
import { daysLeft, sum } from './format';
import { inRange, periodRange } from './periods';
import { deadline, isActive, lastUpdateDate, STALE_DAYS } from './projects';

export interface DashboardProjectRow {
  project: Project;
  hours7: number;
  lastUpdate: string;
  active: boolean;
}
export interface DashboardPerson {
  member: Member;
  hours: number;
  days: number;
  /** project ids ('' = General) worked on in the last 7 days */
  projectIds: string[];
}
export interface DashboardData {
  range: [string, string];
  lastDate: string;
  hoursOnLastDate: number;
  hours7: number;
  activeProjects: number;
  overdue: Project[];
  stale: Project[];
  rows: DashboardProjectRow[];
  people: DashboardPerson[];
}

/** Dashboard figures (the "DashboardStats" contract). */
export type DashboardStats = DashboardData;

export function dashboardData(src: Pick<Backup, 'projects' | 'members' | 'logs' | 'updates'>): DashboardData {
  const R = periodRange('last7');
  const logs7 = src.logs.filter((l) => inRange(l.date, R));
  const dates = [...new Set(src.logs.map((l) => l.date))].sort();
  const last = dates[dates.length - 1] || '';
  const lastH = sum(src.logs.filter((l) => l.date === last).map((l) => l.hours));
  const lastOf = (p: Project) => lastUpdateDate(p.id, src.updates as ProjectUpdate[]);
  const overdue = src.projects.filter((p) => {
    const d = deadline(p);
    const n = d ? daysLeft(d) : null;
    return !!d && n !== null && n < 0 && p.status !== 'Completed';
  });
  const stale = src.projects.filter((p) => isActive(p) && (!lastOf(p) || -(daysLeft(lastOf(p)) ?? 0) >= STALE_DAYS));
  const rows = src.projects.map((p) => ({ project: p, hours7: sum(logs7.filter((l) => l.projectId === p.id).map((l) => l.hours)), lastUpdate: lastOf(p), active: isActive(p) }));
  const people = src.members
    .map((m) => {
      const ml = logs7.filter((l) => l.memberId === m.id);
      return { member: m, hours: sum(ml.map((l) => l.hours)), days: new Set(ml.map((l) => l.date)).size, projectIds: [...new Set(ml.map((l) => l.projectId))] };
    })
    .filter((x) => x.hours > 0)
    .sort((a, b) => b.hours - a.hours);
  return {
    range: R,
    lastDate: last,
    hoursOnLastDate: lastH,
    hours7: sum(logs7.map((l) => l.hours)),
    activeProjects: src.projects.filter(isActive).length,
    overdue,
    stale,
    rows,
    people,
  };
}
